import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Avatar,
  Button,
  Dropdown,
  Layout,
  Menu,
  MenuProps,
  Modal,
  Space,
  Spin,
} from "antd";
import {
  ExclamationCircleOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UserOutlined,
  AuditOutlined,
  MoonOutlined,
  SunOutlined,
  DashboardOutlined,
  TeamOutlined,
  FileAddOutlined,
  FileDoneOutlined,
  CloudUploadOutlined,
  UploadOutlined,
  FolderOpenOutlined,
  BookOutlined,
  ProfileOutlined,
  FolderOutlined,
  QuestionCircleOutlined,
  DatabaseOutlined,
  AppstoreAddOutlined,
  CreditCardOutlined,
  BugOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { ROUTES } from "../../routes/constants";
import { ROLES } from "../../constants/roles";
import { logout } from "../../store/reducers/authSlice";
import { notify } from "../../utils/notification";
import { persistor } from "store";
import { removeTokens } from "utils/authentication";
import ProfileModal from "../../components/profile/ProfileModal";
import { useTheme } from "../../hooks/useTheme";
import { toSentenceCase } from "utils/helperFunctions/textFormatters";
import axiosInstance from "services/axios";
import { API_ENDPOINTS } from "constants/api";
import { useSharePointValidation } from "../../hooks/useSharePointValidation";
import SharePointValidationModal from "../../components/SharePointValidationModal";

type MenuItem = Required<MenuProps>["items"][number];

const { Header, Sider, Content } = Layout;

const DashboardLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { isDark, toggleTheme } = useTheme();
  const [isInternalOrganizationAssigned, setIsInternalOrganizationAssigned] =
    useState(false);
  const [isThirdPartyUsersAssigned, setIsThirdPartyUsersAssigned] =
    useState(false);
  const { loading, user } = useSelector((state: any) => state.auth);

  // Use SharePoint validation hook
  const { showValidationModal, handleSignOut } = useSharePointValidation();
  const path = location.pathname;
  const selectedKey = useMemo(() => {
    if (path.startsWith(ROUTES.DASHBOARD.QMS_ELEMENTS.INTERNAL_ORGANIZATION))
      return "qms-internal";
    if (path.startsWith(ROUTES.DASHBOARD.QMS_ELEMENTS.THIRD_PARTY_USERS))
      return "qms-third-party";
    if (path.startsWith(ROUTES.DASHBOARD.QMS.LIST)) return "qms-other";
    if (path.startsWith(ROUTES.DASHBOARD.USER_MANAGEMENT.USERS)) return "users";
    if (path.startsWith(ROUTES.DASHBOARD.USER_MANAGEMENT.USER_GROUPS))
      return "user-groups";
    if (path.startsWith(ROUTES.DASHBOARD.USER_MANAGEMENT.BASE))
      return "user-management";
    if (path.startsWith(ROUTES.DASHBOARD.OWNER_USER_MANAGEMENT))
      return "owner-user-management";
    if (path.startsWith(ROUTES.DASHBOARD.QMS_ELEMENTS.ELEMENTS))
      return "qms-elements";
    if (path.startsWith(ROUTES.DASHBOARD.FILES)) return "all-files";
    if (path.startsWith(ROUTES.DASHBOARD.REGISTERS)) return "registers";
    if (path.startsWith(ROUTES.DASHBOARD.MASTER_FILES)) return "master-files";
    if (path.startsWith(ROUTES.DASHBOARD.RECORDS)) return "master-files";
    if (path.startsWith(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS))
      return "my-documents";
    if (path.startsWith(ROUTES.DASHBOARD.DOCUMENTS.CREATE))
      return "create-document";
    if (path.startsWith(ROUTES.DASHBOARD.DOCUMENTS.TRACK_REVIEWS))
      return "my-tasks";
    if (path.startsWith(ROUTES.DASHBOARD.DOCUMENTS.LIST)) return "documents";
    if (path.startsWith(ROUTES.DASHBOARD.HELPDESK)) return "helpdesk";
    if (path.startsWith(ROUTES.DASHBOARD.TENANT_MANAGEMENT.LIST))
      return "tenant-management";
    if (path.startsWith(ROUTES.DASHBOARD.SUBSCRIPTION_BILLING))
      return "subscription-billing";
    if (path.startsWith(ROUTES.DASHBOARD.SUBSCRIPTION_BILLING_VIEW_INVOICES))
      return "invoices";
    if (path.startsWith(ROUTES.DASHBOARD.QUALITY_ISSUES))
      return "quality-issues";
    if (path.startsWith(ROUTES.DASHBOARD.HOME)) return "Dashboard";
    return "Dashboard";
  }, [path]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!user?.id) return;
      try {
        const User = await axiosInstance.get(
          API_ENDPOINTS.USERS.GET_USER_DETAILS(user.id),
        );
        const isInternalOrganizationAssigned = User.data.data?.element?.some(
          (el: any) => el.qmsElement?.name === "Internal Organization",
        );
        setIsInternalOrganizationAssigned(isInternalOrganizationAssigned);
        const isThirdPartyUsersAssigned = User.data.data?.element?.some(
          (el: any) => el.qmsElement?.name === "Third Party Users",
        );
        setIsThirdPartyUsersAssigned(isThirdPartyUsersAssigned);
        // You can use these variables as needed
      } catch (error) {
        // Handle error if needed
      }
    };
    fetchUserDetails();
  }, [user]);

  const handleLogout = () => {
    Modal.confirm({
      title: "Sign Out",
      icon: <ExclamationCircleOutlined />,
      content: "Are you sure you want to sign out?",
      async onOk() {
        try {
          dispatch(logout());
          removeTokens();
          await persistor.purge();
          await persistor.flush();
          notify.success({
            message: "Signed Out Successfully",
            description: "You have been successfully signed out.",
          });
          navigate(ROUTES.AUTH.LOGIN);
        } catch (error) {
          notify.error({
            message: "Sign Out Failed",
            description: "There was an error signing out. Please try again.",
          });
        }
      },
    });
  };
  const profileMenuItems: MenuItem[] = [
    {
      key: "profile",
      label: (
        <Space>
          <UserOutlined />
          {user?.name || "Profile"}
        </Space>
      ),
      onClick: () => setShowProfileModal(true),
    },
    // {
    //   key: 'theme',
    //   label: (
    //     <Space>
    //       {isDark ? <SunOutlined /> : <MoonOutlined />}
    //       {isDark ? 'Light Mode' : 'Dark Mode'}
    //     </Space>
    //   ),
    //   onClick: toggleTheme,
    // },
    {
      key: "divider",
      type: "divider",
    },
    {
      key: "logout",
      label: (
        <Space>
          <LogoutOutlined style={{ color: "red" }} />
          <span style={{ color: "red" }}>Sign Out</span>
        </Space>
      ),
      onClick: handleLogout,
    },
  ];
  const getMenuItems = useCallback((): MenuItem[] => {
    const items: MenuItem[] = [
      {
        key: "Dashboard",
        icon: <DashboardOutlined />,
        label: "Dashboard",
        onClick: () => navigate(ROUTES.DASHBOARD.HOME),
      },
    ];

    const userRole = user?.roles[0].name;

    // Super Admin specific menu items
    if (userRole === ROLES.SUPER_ADMIN) {
      items.push(
        {
          key: "tenant-management",
          icon: <AppstoreAddOutlined />,
          label: "Tenant Management",
          onClick: () => navigate(ROUTES.DASHBOARD.TENANT_MANAGEMENT.LIST),
        },
        {
          key: "subscription-billing",
          icon: <CreditCardOutlined />,
          label: "Billing & Invoices",
          onClick: () => navigate(ROUTES.DASHBOARD.SUBSCRIPTION_BILLING),
        },
      );
    }

    // Common QMS Elements for all roles except ARCHIVIST only
    if (userRole === ROLES.ARCHIVIST) {
      items.push(
        {
          key: "qms-elements",
          icon: <FolderOutlined />,
          label: "QMS Elements",
          onClick: () => navigate(ROUTES.DASHBOARD.QMS_ELEMENTS.ELEMENTS),
        },
        // {
        //   key: "master-files",
        //   icon: <DatabaseOutlined />,
        //   label: "Master Files",
        //   onClick: () => navigate(ROUTES.DASHBOARD.MASTER_FILES),
        // },
        // {
        //   key: "qms-list",
        //   icon: <FolderOutlined />,
        //   label: "QMS List",
        //   onClick: () => navigate(ROUTES.DASHBOARD.QMS.LIST),
        // },
      );
    }

    if (userRole === ROLES.OWNER || userRole === ROLES.MANAGER) {
      const qmsChildren: MenuItem[] = [];

      // Add Internal Organization if assigned
      if (isInternalOrganizationAssigned) {
        qmsChildren.push({
          key: "qms-internal",
          icon: <ProfileOutlined />,
          label: "Internal Organization",
          onClick: () =>
            navigate(ROUTES.DASHBOARD.QMS_ELEMENTS.INTERNAL_ORGANIZATION),
        });
      }

      // Add Third Party Users if assigned
      if (isThirdPartyUsersAssigned) {
        qmsChildren.push({
          key: "qms-third-party",
          icon: <TeamOutlined />,
          label: "Third Party Users",
          onClick: () =>
            navigate(ROUTES.DASHBOARD.QMS_ELEMENTS.THIRD_PARTY_USERS),
        });
      }

      // Always add Other Elements/Assigned Folders
      qmsChildren.push({
        key: "qms-other",
        icon: <FolderOpenOutlined />,
        label:
          isInternalOrganizationAssigned || isThirdPartyUsersAssigned
            ? "Other Elements"
            : "Assigned Folders",
        onClick: () => navigate(ROUTES.DASHBOARD.QMS_ELEMENTS.ELEMENTS),
      });

      items.push({
        key: "qms-elements",
        icon: <FolderOutlined />,
        label: "QMS Elements",
        children: qmsChildren,
      });
    }

    // ARCHIVIST specific menu items
    if (userRole === ROLES.ARCHIVIST) {
      items.push(
        {
          key: "user-management",
          icon: <TeamOutlined />,
          label: "User Management",
          children: [
            // {
            //   key: "users",
            //   label: "Users",
            //   icon: <UserOutlined />,
            //   onClick: () => navigate(ROUTES.DASHBOARD.USER_MANAGEMENT.USERS),
            // },
            {
              key: "user-groups",
              label: "User Groups",
              icon: <TeamOutlined />,
              onClick: () =>
                navigate(ROUTES.DASHBOARD.USER_MANAGEMENT.USER_GROUPS),
            },
          ],
        },
        {
          key: "invoices",
          icon: <CreditCardOutlined />,
          label: "Invoices",
          onClick: () =>
            navigate(
              ROUTES.DASHBOARD.SUBSCRIPTION_BILLING_VIEW_INVOICES.replace(
                ":tenantId",
                user?.company?.id || "",
              ),
            ),
        },
      );
    } // OWNER specific menu sequence: Dashboard, QMS Elements, User Management, Documents, Records
    if (userRole === ROLES.OWNER) {
      items.push(
        // {
        //   key: 'owner-user-management',
        //   icon: <TeamOutlined />,
        //   label: 'User Management',
        //   onClick: () => navigate(ROUTES.DASHBOARD.OWNER_USER_MANAGEMENT),
        // },
        // {
        //   key: "registers",
        //   icon: <ProfileOutlined />,
        //   label: "Registers",
        //   onClick: () => navigate(ROUTES.DASHBOARD.REGISTERS),
        // },
        // {
        //   key: 'master-files',
        //   icon: <DatabaseOutlined />,
        //   label: 'Master Files',
        //   onClick: () => navigate(ROUTES.DASHBOARD.MASTER_FILES),
        // },
        // {
        //   key: 'documents',
        //   icon: <FolderOpenOutlined />,
        //   label: 'Documents',
        //   children: [
        //     {
        //       key: 'my-documents',
        //       label: 'My Documents',
        //       icon: <FileDoneOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        //     },
        //     {
        //       key: 'create-document',
        //       label: 'Document Creation',
        //       icon: <FileAddOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.CREATE),
        //     },
        //   ],
        // },
        {
          key: "my-documents",
          label: "My Documents",
          icon: <FileDoneOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        },
        {
          key: "my-tasks",
          label: "My Tasks",
          icon: <AuditOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.TRACK_REVIEWS),
        },
      );
    } // MANAGER specific menu sequence: Dashboard, QMS Elements, Registers, Master Files, Documents, Records
    if (userRole === ROLES.MANAGER) {
      items.push(
        // {
        //   key: "registers",
        //   icon: <ProfileOutlined />,
        //   label: "Registers",
        //   onClick: () => navigate(ROUTES.DASHBOARD.REGISTERS),
        // },
        {
          key: "master-files",
          icon: <DatabaseOutlined />,
          label: "Master Files",
          onClick: () => navigate(ROUTES.DASHBOARD.MASTER_FILES),
        },
        // {
        //   key: 'documents',
        //   icon: <FolderOpenOutlined />,
        //   label: 'Documents',
        //   children: [
        //     {
        //       key: 'my-documents',
        //       label: 'My Documents',
        //       icon: <FileDoneOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        //     },
        //     {
        //       key: 'create-document',
        //       label: 'Document Creation',
        //       icon: <FileAddOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.CREATE),
        //     },
        //   ],
        // },
        {
          key: "my-documents",
          label: "My Documents",
          icon: <FileDoneOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        },
        {
          key: "my-tasks",
          label: "My Tasks",
          icon: <AuditOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.TRACK_REVIEWS),
        },
      );

      // Add Quality Issues menu item for QA managers (IDs starting with "qa@")
      if (user?.email && user.email.startsWith("qa@")) {
        items.push({
          key: "quality-issues",
          label: "Quality Issues",
          icon: <BugOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.QUALITY_ISSUES),
        });
      }
    } // TEAM_MEMBER specific menu sequence: Dashboard, QMS Elements, Master Files, Documents, Records
    if (userRole === ROLES.TEAM_MEMBER) {
      items.push(
        {
          key: "master-files",
          icon: <DatabaseOutlined />,
          label: "Master Files",
          onClick: () => navigate(ROUTES.DASHBOARD.MASTER_FILES),
        },
        // {
        //   key: 'documents',
        //   icon: <FolderOpenOutlined />,
        //   label: 'Documents',
        //   children: [
        //     {
        //       key: 'my-documents',
        //       label: 'My Documents',
        //       icon: <FileDoneOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        //     },
        //     {
        //       key: 'create-document',
        //       label: 'Document Creation',
        //       icon: <FileAddOutlined />,
        //       onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.CREATE),
        //     },
        //   ],
        // },
        {
          key: "my-documents",
          label: "My Documents",
          icon: <FileDoneOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.MY_DOCUMENTS),
        },
        {
          key: "my-tasks",
          label: "My Tasks",
          icon: <AuditOutlined />,
          onClick: () => navigate(ROUTES.DASHBOARD.DOCUMENTS.TRACK_REVIEWS),
        },
      );
    } // OBSERVER specific menu sequence: Dashboard, Master Files
    if (userRole === ROLES.OBSERVER) {
      items.push({
        key: "master-files",
        icon: <DatabaseOutlined />,
        label: "Master Files",
        onClick: () => navigate(ROUTES.DASHBOARD.MASTER_FILES),
      });
    }

    // Add Helpdesk for all users
    items.push({
      key: "helpdesk",
      icon: <QuestionCircleOutlined />,
      label: "Helpdesk",
      onClick: () => navigate(ROUTES.DASHBOARD.HELPDESK),
    });

    return items;
  }, [
    navigate,
    user?.roles,
    user?.company?.id,
    user?.email,
    isInternalOrganizationAssigned,
    isThirdPartyUsersAssigned,
  ]);

  const menuItems = useMemo(() => getMenuItems(), [getMenuItems]);

  if (loading) {
    return (
      <div className="dashboard-layout__loading">
        <Spin size="large" tip="Loading configuration..." />
      </div>
    );
  }

  return (
    <Layout className="dashboard-layout">
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        className="dashboard-layout__sider"
        width={250}
      >
        <div className="dashboard-layout__logo">ITSQA</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
        />
        {/* Version info at the bottom */}
        <div
          className="w-full text-center text-xs text-gray-400 mt-auto mb-5"
          style={{
            position: "absolute",
            bottom: 0,
            width: "100%",
          }}
        >
          <span className="block">Version 1.0.7</span>
          <span className="block">Release: 04-Nov-2025</span>
        </div>
      </Sider>
      <Layout>
        <Header className="dashboard-layout__header">
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            className="dashboard-layout__trigger"
          />
          <div className="flex justify-center items-center">
            <h5 className="name text-black">
              {user?.roles[0].name === ROLES.ARCHIVIST ? (
                user?.roles[0].name
              ) : (
                <>
                  {toSentenceCase(user?.firstName) +
                    " " +
                    toSentenceCase(user?.lastName)}
                  <span className="dashboard-layout__userRole">
                    {user?.roles[0].name}
                  </span>
                </>
              )}
            </h5>
            <div className="dashboard-layout__profile">
              <Dropdown
                menu={{ items: profileMenuItems }}
                placement="bottomRight"
                trigger={["click"]}
              >
                <Space className="dashboard-layout__user">
                  <Avatar icon={<UserOutlined />} />
                  {!collapsed && <span>{user?.name}</span>}
                </Space>
              </Dropdown>
            </div>
          </div>
        </Header>
        {/* <Content className="dashboard-layout__content">
          <Outlet />
        </Content> */}
        <Content className="dashboard-layout__content">
          <Outlet />
        </Content>
      </Layout>
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        user={user}
      />
      <SharePointValidationModal
        visible={showValidationModal}
        onSignOut={handleSignOut}
      />
    </Layout>
  );
};

export default DashboardLayout;
